import { getClassIdToNameMap } from 'base/webflow/helpers';
import { toNested } from 'base/webflow/nested';
import {
  Component,
  ComponentStyle,
  InlineStyleData,
  NestedNode,
  NestedTagNode,
} from 'base/webflow/schema';
import { styleGuide, styleGuideStyles } from 'base/webflow/style_guide';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { BoundsMeasurer } from './bounds';
import rendererStyles from './component_renderer.css';
import {
  AddEffect,
  BreakpointContext,
  ComponentContext,
  NodeDecorator,
  WrapFunction,
} from './decorators';
import { transformNodeAttr } from './node_attrs';
import normalizeStyles from './normalize_scoped.css';
import {
  breakpointClassName,
  ComponentCss,
  getBreakpointForViewport,
  VariableDefaultsCss,
} from './styles';
import webflowStyles from './webflow_base.css';

let __idCounter = 0;

/** Hook to return a unique css class name */
export function useRootClassName() {
  const [id] = React.useState(() => __idCounter++);
  return `cr-${id}`;
}

export const NoSlotsComponentView = observer(function _NoSlotsComponentView({
  component,
  outerWidth,
  innerWidth,
  innerViewHeight,
  decorator,
  overlays,
}: {
  component: Component;
  outerWidth: number;
  innerWidth: number;
  innerViewHeight: number;
  decorator: NodeDecorator;
  overlays?: React.ReactNode;
}) {
  const rootClassName = useRootClassName();

  const [bounds] = React.useState(() => new BoundsMeasurer());
  bounds.ingestStyles(component.payload.styles);
  bounds.ingestStyles(styleGuideStyles);

  const nodes = React.useMemo(
    () =>
      toNested(component.payload.nodes).map((node) =>
        removeDeleteSuffixedNodes(node, component.payload.styles),
      ),
    [component],
  );

  const breakpoints = React.useMemo(
    () => getBreakpointForViewport(innerWidth),
    [innerWidth],
  );

  const ctx = React.useMemo<ComponentContext>(
    () => ({
      classNames: getClassIdToNameMap([
        ...styleGuideStyles,
        ...component.payload.styles,
      ]),
      ix2: component.payload.ix2,
      bounds,
      breakpoints,
    }),
    [component.payload.ix2, component.payload.styles, bounds, breakpoints],
  );

  return (
    <ComponentContainer
      {...{
        overlays,
        rootClassName,
        outerWidth,
        innerWidth,
        innerViewHeight,
        bounds,
        breakpoints,
      }}
    >
      {nodes.map((n) => (
        <AnyNodeView decorator={decorator} node={n} ctx={ctx} key={n._id} />
      ))}
      <ComponentCss
        styles={component.payload.styles}
        assets={component.payload.assets}
        rootClassName={rootClassName}
        cacheKey={`${rootClassName}-component-payload`}
      />
      <ComponentCss
        styles={styleGuideStyles}
        assets={styleGuide.files}
        rootClassName={rootClassName}
        cacheKey={`${rootClassName}-style-guide`}
      />
      <VariableDefaultsCss
        rootClassName={rootClassName}
        variables={styleGuide.variables || []}
      />
    </ComponentContainer>
  );
});

/** Scales & bounds a component, roots css, prevents overflow, margin collapse, etc. */
export const ComponentContainer = observer(function _ComponentContainer({
  children,
  overlays,
  rootClassName,
  outerWidth,
  innerWidth,
  innerViewHeight,
  bounds,
  breakpoints,
  useContainerBounds,
}: {
  children: React.ReactNode;
  overlays?: React.ReactNode;
  rootClassName: string;
  outerWidth: number;
  innerWidth: number;
  innerViewHeight: number;
  bounds: BoundsMeasurer | undefined;
  breakpoints: BreakpointContext;
  useContainerBounds?: boolean;
}) {
  const innerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!bounds) return;
    bounds.attach('container', innerRef);
  }, [innerRef, bounds]);

  const componentBounds = useContainerBounds
    ? bounds?.containerBounds
    : bounds?.bounds;

  return (
    <div
      className={rendererStyles.componentOuter}
      style={{
        width: outerWidth,
        height: componentBounds
          ? componentBounds.height * (outerWidth / innerWidth)
          : '100%',
      }}
      {...{ [COMPONENT_RENDERER_OUTER_DATASET_KEY]: outerWidth / innerWidth }}
    >
      <div
        ref={innerRef}
        className={classNames(
          rendererStyles.componentInner,
          normalizeStyles.normalize,
          webflowStyles.root,
          rootClassName,
          breakpointClassName(breakpoints.current),
          breakpoints.larger.map(breakpointClassName),
        )}
        style={{
          width: innerWidth,
          transform: `scale(${outerWidth / innerWidth}) translateY(${-(
            componentBounds?.top || 0
          )}px)`,
          ['--vw' as any]: innerWidth / 100 + 'px',
          ['--vh' as any]: innerViewHeight / 100 + 'px',
        }}
        {...{
          [COMPONENT_RENDERER_INNER_DATASET_KEY]: '1',
        }}
      >
        {children}
      </div>
      {overlays}
    </div>
  );
});

export const COMPONENT_RENDERER_OUTER_DATASET_KEY = 'data-rlcrout';
export const COMPONENT_RENDERER_INNER_DATASET_KEY = 'data-rlcrin';

type NodeViewProps = {
  node: NestedNode;
  decorator: NodeDecorator;
  ctx: ComponentContext;
};

export const AnyNodeView = observer(function _AnyNodeView({
  node,
  decorator,
  ctx,
}: NodeViewProps) {
  if (node.text) return <>{node.v}</>;
  return <TagNodeView node={node} decorator={decorator} ctx={ctx} />;
});

const TagNodeView = observer(function _TagNodeView({
  node,
  decorator,
  ctx,
}: NodeViewProps & { node: NestedTagNode }) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => ctx.bounds.attach(node, ref), [ref, node, ctx]);

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let effects: AddEffect[] = [];
  React.useEffect(() => {
    const el = ref.current;
    if (!el || node.text) return;
    const disposers = effects.map((e) => e(node, ctx, el));
    return () => {
      for (const disposer of disposers) disposer();
    };
  }, [node, ctx, effects]);

  let classes: classNames.ArgumentArray = node.classes.map(
    (id) => ctx.classNames.get(id) || id,
  );
  const style = node.data.style
    ? computeInlineStyle(node.data.style, ctx.breakpoints)
    : {};
  let children: React.ReactNode = node.children.map((child) => (
    <AnyNodeView node={child} decorator={decorator} ctx={ctx} key={child._id} />
  ));
  const attrs = transformNodeAttr(node.data.attr);

  let wrapFn: WrapFunction | undefined;

  let nodeV = node.v;
  for (const decoration of decorator(node, ctx)) {
    if (decoration.className) classes.push(decoration.className);
    if (decoration.replaceClassNames)
      classes = [...decoration.replaceClassNames(classes)];
    if (decoration.style) Object.assign(style, decoration.style);
    if (decoration.attrs) Object.assign(attrs, decoration.attrs);
    if (decoration.wrapChildren) children = decoration.wrapChildren(children);
    if (decoration.replaceValue) nodeV = decoration.replaceValue;
    if (decoration.addEffect) effects.push(decoration.addEffect);
    if (decoration.wrap) wrapFn = decoration.wrap;
  }

  let Tag = node.tag as 'div';

  if (node.tag === 'input' && node.data.inputType === 'custom') {
    // If custom styles were applied to a checkbox or radio button in Webflow,
    // render it as a <div> instead.
    Tag = 'div';
  }

  if (
    node.tag === 'div' &&
    classes.some((c) => c?.toString().endsWith('_background-video'))
  ) {
    // Render background video placeholders as img tags so we can style them in
    // the same way as images in the style guide builder.
    Tag = 'img' as any;
  }

  // e.g. an <img/> tag can't have children, not even []
  if (React.Children.count(children) === 0) children = undefined;
  if (node.type === 'HtmlEmbed') {
    if (children) console.warn('HTMLEmbed had children', node, children);
    attrs.dangerouslySetInnerHTML = { __html: nodeV || '' };
    children = undefined;
  }

  return (
    <>
      {wrapFn?.(
        <Tag {...attrs} className={classNames(classes)} style={style} />,
        node,
        ctx,
        ref,
        classes,
      ) ?? (
        <Tag {...attrs} className={classNames(classes)} style={style} ref={ref}>
          {children}
        </Tag>
      )}
    </>
  );
});

function computeInlineStyle(
  { base: byBreakpoint }: InlineStyleData,
  breakpoints: ComponentContext['breakpoints'],
): React.CSSProperties {
  let result: React.CSSProperties = {};
  const breakpointsToApply = [
    breakpoints.current,
    ...breakpoints.larger,
  ].reverse();
  for (const bp of breakpointsToApply) {
    const style = byBreakpoint?.[bp];
    if (!style) continue;
    Object.assign(result, style.noPseudo);
  }
  return result;
}

const DELETE_SUFFIX = ' (Delete)';
const styleGuideDeleteClassIds = styleGuideStyles
  .filter((s) => s.name.endsWith(DELETE_SUFFIX))
  .map((s) => s._id);

/**
 * Removes any nodes that have a class suffixed in " (Delete)".
 *
 * In Relume Library components, this is used for nodes that contain instructions.
 */
export function removeDeleteSuffixedNodes(
  root: NestedNode,
  componentStyles: readonly ComponentStyle[],
): NestedNode {
  if (root.text) return root;

  const deleteClassIds = componentStyles
    .filter((s) => s.name.endsWith(DELETE_SUFFIX))
    .map((s) => s._id)
    .concat(styleGuideDeleteClassIds);
  if (deleteClassIds.length === 0) return root;

  function visit(n: NestedNode): NestedNode | NestedNode[] {
    if (n.text) {
      return n;
    }
    if (n.classes.some((id) => deleteClassIds.includes(id))) {
      return [];
    }
    return { ...n, children: n.children.flatMap(visit) };
  }

  return { ...root, children: root.children.flatMap(visit) };
}
